'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export type MHGridPreset = 'full' | 'split' | 'wide' | 'three' | 'aside';
export type MHGridGap = 8 | 12 | 16 | 24 | 32 | 48;
export type MHGridArea = 'full' | 'main' | 'side' | 'sidebar';

const mhGridVariants = cva('grid w-full', {
  variants: {
    preset: {
      full: 'grid-cols-1',
      split: 'grid-cols-1 lg:grid-cols-12',
      wide: 'grid-cols-1 lg:grid-cols-12',
      three: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      aside: 'grid-cols-1 lg:grid-cols-12',
    },
    gap: {
      8: 'gap-2',
      12: 'gap-3',
      16: 'gap-4',
      24: 'gap-6',
      32: 'gap-8',
      48: 'gap-12',
    },
  },
  defaultVariants: {
    preset: 'full',
    gap: 16,
  },
});

const presetContext = React.createContext<MHGridPreset>('full');

export interface MHGridProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof mhGridVariants> {}

const MHGrid = React.forwardRef<HTMLDivElement, MHGridProps>(
  ({ className, preset, gap, ...props }, ref) => (
    <presetContext.Provider value={preset ?? 'full'}>
      <div
        ref={ref}
        className={cn(mhGridVariants({ preset, gap }), className)}
        {...props}
      />
    </presetContext.Provider>
  )
);

MHGrid.displayName = 'MHGrid';

const mhGridItemVariants = cva('', {
  variants: {
    area: {
      full: '',
      main: '',
      side: '',
      sidebar: '',
    },
  },
  defaultVariants: {
    area: 'full',
  },
});

const getAreaClasses = (preset: MHGridPreset, area: MHGridArea) => {
  if (preset === 'split') {
    if (area === 'main') return 'lg:col-span-7';
    if (area === 'side') return 'lg:col-span-5';
  }

  if (preset === 'wide') {
    if (area === 'main') return 'lg:col-span-8';
    if (area === 'side') return 'lg:col-span-4';
  }

  if (preset === 'aside') {
    if (area === 'sidebar') return 'lg:col-span-3';
    if (area === 'main') return 'lg:col-span-9';
  }

  return '';
};

export interface MHGridItemProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof mhGridItemVariants> {}

const MHGridItem = React.forwardRef<HTMLDivElement, MHGridItemProps>(
  ({ className, area, ...props }, ref) => {
    const preset = React.useContext(presetContext);

    return (
      <div
        ref={ref}
        className={cn(
          mhGridItemVariants({ area }),
          getAreaClasses(preset, area ?? 'full'),
          className
        )}
        {...props}
      />
    );
  }
);

MHGridItem.displayName = 'MHGridItem';

export { MHGrid, MHGridItem, mhGridVariants, mhGridItemVariants };
